import { VideoBasicBoldIcon } from '@depop/web-ui-kit/Icons/VideoBasicBoldIcon';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useTranslations } from 'next-intl';

import { ProductCardLike } from '../ProductCard/ProductCardLike';
import overlayStyles from '../ProductCard/overlay.module.css';

import styles from './styles.module.css';

import { ProductCard } from '@/components/ProductCard';
import { ProductCardAttributes } from '@/components/ProductCardAttributes';
import { interactionTypes } from '@/modules/activityTracker/constants';
import {
  buildProductImageProps,
  picturePreviewAdapter,
} from '@/modules/pictures/helpers';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';
import { type SimilarProduct } from '@/modules/similar/aliases';
import { getSizeNames } from '@/modules/similar/helpers/getSizeNames';

type Props = {
  product: SimilarProduct;
  id: SimilarProduct['id'];
  slug: SimilarProduct['slug'];
  pricing: SimilarProduct['pricing'];
  isBoosted?: SimilarProduct['is_boosted'];
  onClick?: (product: SimilarProduct) => void;
  showBrandName?: boolean;
};

export function SimilarItemsProductCard({
  product,
  onClick = () => null,
  showBrandName = true,
}: Props) {
  const t = useTranslations('common');
  const { sendProductInteractionActionATEvent } = useProductTracking();

  const hasMoreThanOnePicture = (product.pictures?.length ?? 0) > 1;
  const [sellerUsername] = product.slug.split('-');
  const hasVideo = Boolean(product.videos?.length);
  const conditionalProps = {
    Overlay: () => (
      <div className={overlayStyles.gradientOverlay}>
        {product.id && (
          <ProductCardLike productId={product.id} isLiked={product.is_liked} />
        )}
        {hasVideo && (
          <div className={styles.videoIcon}>
            <VideoBasicBoldIcon />
          </div>
        )}
      </div>
    ),
    ...(hasMoreThanOnePicture && {
      HoverOverlay: () => (
        <LazyLoadImage
          {...buildProductImageProps({
            preview: picturePreviewAdapter(product.pictures?.[1]),
            sellerUserName: sellerUsername,
          })}
        />
      ),
    }),
  };

  function handleProductClick() {
    sendProductInteractionActionATEvent(
      product.id,
      interactionTypes.PRODUCT_SIMILAR_ITEMS
    );
    onClick(product);
  }

  return (
    <ProductCard
      productHref={`${product.slug}`}
      preview={picturePreviewAdapter(product.pictures?.[0])}
      handleClick={handleProductClick}
      sellerUserName={sellerUsername}
      ProductAttributes={() => (
        <ProductCardAttributes
          sizes={getSizeNames(product.sizes)}
          brandName={
            showBrandName ? product.brand_name || t('Other') : undefined
          }
          displayPrice={product.pricing.display_price}
        />
      )}
      {...conditionalProps}
    />
  );
}
